import type { MouseEvent, KeyboardEvent as ReactKeyboardEvent } from "react";

type KeyEvent = ReactKeyboardEvent | KeyboardEvent;
type KeyHandler = (e?: KeyEvent | MouseEvent) => void;

function matchesKey(event: KeyEvent, keyCode: number, lowerKey: string | undefined) {
	// Autofill in some browsers dispatches keydown events without a key.
	return (!!lowerKey && lowerKey === event.key?.toLowerCase()) || event.keyCode === keyCode;
}

/** Fires if specific key is pressed with exactly the specified combo keys */
export const comboKeyPress =
	(ctrl: boolean, alt: boolean = false, shift: boolean = false) =>
	(keyCode: number, key?: string) => {
		const lowerKey = key?.toLowerCase();
		return (fn: KeyHandler) => (event: KeyEvent) => {
			const modifiersValid = ctrl === event.ctrlKey && alt === event.altKey && shift === event.shiftKey;
			if (modifiersValid && matchesKey(event, keyCode, lowerKey)) fn(event);
		};
	};

/** Fires if specific key is pressed with no combo key */
export const soloKeyPress = comboKeyPress(false, false, false);
/** Fires if specific key is pressed, regardless of combo keys */
export function specificKeyPress(keyCode: number, key?: string) {
	const lowerKey = key?.toLowerCase();
	return function (fn: KeyHandler) {
		return function (event: KeyEvent) {
			if (matchesKey(event, keyCode, lowerKey)) fn(event);
		};
	};
}

/** soloKeyPress when solo is true, otherwise specificKeyPress (combo keys ignored) */
export const optionalSoloKeyPress = (solo?: boolean) => (solo ? soloKeyPress : specificKeyPress);

/** onEnter fn happens when enter key pressed */
export const enterKey = soloKeyPress(13, "Enter");
/** onTab fn happens when tab key pressed */
export const tabKey = soloKeyPress(9, "Tab");
/** onPgUp fn happens when page up key pressed */
export const pgUpKey = soloKeyPress(33, "PageUp");
/** onPgDown fn happens when page down key pressed */
export const pgDownKey = soloKeyPress(34, "PageDown");

/** Concatenates keyDown listeners */
export const keyListeners =
	(...listeners: ((event: KeyEvent) => void)[]) =>
	(event: KeyEvent) => {
		listeners.forEach((l) => l(event));
	};
